import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchProductsUsingFilter } from '../../../admin/controllers/products-controller';
import BrandController from '../../../admin/controllers/brand-controller';
import { isStockRequired } from '../../../../utils/stock';

export const useHomePageViewModel = () => {
  const PAGE_SIZE = 10;

  // Products state
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadMoreError, setLoadMoreError] = useState(null);

  // Filter states
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Brand data
  const [brands, setBrands] = useState([]);

  // Infinite scroll state
  const [activeFilters, setActiveFilters] = useState({ disponible: true });
  const pageRef = useRef(1);

  const formatProducts = useCallback((productsList = []) => productsList.map(product => ({
    id: product.id,
    name: product.name || 'Unnamed Product',
    price: product.prixVenteTTC || 0,
    originalPrice: product.prixAchatTTC || 0,
    image: product.imageUrl && product.imageUrl.length > 0 && product.imageUrl[0]
      ? product.imageUrl[0]
      : 'https://via.placeholder.com/300',
    rating: 4.5,
    description: product.description || '',
    marque: product.marque || null,
    brand: product.marque ? product.marque.name : '',
    brandId: product.marqueId || null,
    inStock: !isStockRequired(product) || product.stock > 0,
    stockRequis: isStockRequired(product),
    isVariant: product.isVariant || false,
    variants: product.productVariants || [],
    reference: product.reference || '',
    enPromo: product.enPromo ?? false,
    prixVentePromo: product.prixVentePromo ?? 0,
    variantImages: product.productVariants && product.productVariants.length > 0
      ? product.productVariants.map(v => ({
        id: v.id,
        name: v.name,
        image: v.imageUrl && v.imageUrl.length > 0 && v.imageUrl[0] ? v.imageUrl[0] : null,
        stock: v.stock || 0
      }))
      : []
  })), []);

  const normalizeBrand = (brand) => ({
    id: brand?.id ?? brand?.Id ?? null,
    name: brand?.name ?? brand?.Name ?? '',
    imagePath: brand?.imagePath ?? brand?.ImagePath ?? null,
  });

  const normalizeFilters = useCallback((filterParams = {}) => {
    const filters = {
      disponible: true,
      ...filterParams
    };

    if (filters.search === '') {
      delete filters.search;
    }

    if (filters.marqueId === null || filters.marqueId === '' || filters.marqueId === 'all') {
      delete filters.marqueId;
    }

    return filters;
  }, []);

  const requestProducts = useCallback(async ({ page = 1, append = false, filters = {} } = {}) => {
    const requestFilters = normalizeFilters(filters);
    const requestParams = {
      page,
      pageSize: PAGE_SIZE,
      ...requestFilters
    };

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setLoadMoreError(null);
    }

    setError(null);

    try {
      const response = await fetchProductsUsingFilter(requestParams);

      if (!response || !response.data || !response.data.list) {
        throw new Error('Invalid response format from server');
      }

      const productsList = response.data.list;
      const formattedProducts = formatProducts(productsList);

      pageRef.current = page;
      const total = response.data?.total ?? response.totalProduct ?? 0;
      setHasMore(page * PAGE_SIZE < total);

      setProducts(prevProducts => append ? [...prevProducts, ...formattedProducts] : formattedProducts);
    } catch (err) {
      console.error('Error fetching products with filters:', err);

      if (append) {
        setLoadMoreError('Failed to load more products.');
        setHasMore(false);
      } else {
        setError('Failed to load products. Please try again later.');
        setProducts([]);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [formatProducts, normalizeFilters]);

  // Load initial products and brands
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setLoadMoreError(null);

      // Fetch brands
      const brandsResponse = await BrandController.fetchAllBrands();
      console.log("Brands response:", brandsResponse);
      const normalizedBrands = Array.isArray(brandsResponse)
        ? brandsResponse.map(normalizeBrand).filter(brand => brand.id !== null)
        : [];
      setBrands(normalizedBrands);

      setSelectedCategory('all');
      setActiveFilters({ disponible: true });
      setProducts([]);
      setHasMore(true);
      pageRef.current = 1;

      await requestProducts({ page: 1, append: false, filters: { disponible: true } });

    } catch (err) {
      console.error("Error loading initial data:", err);
      setError("Failed to load products. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [requestProducts]);

  const applyFilters = useCallback((filterParams = {}) => {
    const filters = {
      marqueId: filterParams.marqueId !== undefined ? filterParams.marqueId : selectedCategory,
      disponible: true
    };

    console.log("Applying filters:", filters);

    setActiveFilters(filters);

    setHasMore(true);
    setLoadMoreError(null);
    pageRef.current = 1;
    setProducts([]);

    requestProducts({ page: 1, append: false, filters });
  }, [requestProducts, selectedCategory]);

  // Reset filters
  const resetFilters = () => {
    setSelectedCategory('all');
    setActiveFilters({ disponible: true });
    setHasMore(true);
    setLoadMoreError(null);
    pageRef.current = 1;
    setProducts([]);

    requestProducts({ page: 1, append: false, filters: { disponible: true } });
  };

  const loadMoreProducts = useCallback(() => {
    if (loading || loadingMore || !hasMore) {
      return;
    }

    const nextPage = pageRef.current + 1;
    requestProducts({ page: nextPage, append: true, filters: activeFilters });
  }, [activeFilters, hasMore, loading, loadingMore, requestProducts]);

  const retryLoadMore = useCallback(() => {
    setHasMore(true);
    setLoadMoreError(null);
    loadMoreProducts();
  }, [loadMoreProducts]);

  // Load data when component mounts
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Get products list (server already filters by marque/disponible)
  const filteredProducts = useMemo(() => {
    return [...products];
  }, [products]);

  return {
    // Products state
    products,
    loading,
    error,

    // Filter states
    selectedCategory,
    setSelectedCategory,

    // Brand data
    brands,

    // Infinite scroll
    hasMore,
    loadingMore,
    loadMoreProducts,
    loadMoreError,
    retryLoadMore,

    // Computed values
    filteredProducts,

    // Actions
    resetFilters,
    applyFilters,

    // Initial loading
    loadInitialData
  };
};